import React, { useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CommonLoader from './CommonLoader';
import {
  faSpinner,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import './ResponsiveTable.css'; // We'll create this CSS file

export default function Table({
  columns,
  data,
  color,
  loading,
  totalPage,
  currentpage,
  setPage,
  setSortBy,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            pageIndex: currentpage,
          }),
          [state]
        );
      },
      initialState: { pageIndex: currentpage },
      manualPagination: true,
      pageCount: totalPage,
      manualSortBy: true,
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (setSortBy) setSortBy(sortBy);
  }, [sortBy]);

  return (
    <>
      {/* Responsive Table Container */}
      <div className="responsive-table-wrapper">
        <div className="responsive-table-container">
          <table
            {...getTableProps()}
            className="table is-striped is-hoverable is-fullwidth responsive-table"
          >
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr 
                  {...headerGroup.getHeaderGroupProps()}
                  className="responsive-table-header"
                >
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps([
                        {
                          className: column.className,
                          style: column.style,
                        },
                        column.getSortByToggleProps(),
                      ])}
                      className={`responsive-table-th ${column.className || ''}`}
                      style={{
                        borderRight: index < headerGroup.headers.length - 1 
                          ? '1px solid rgba(0,0,0,0.08)' 
                          : 'none',
                        ...column.style
                      }}
                    >
                      <div className="responsive-header-content">
                        <span className="header-text">{column.render("Header")}</span>
                        <span className="sort-indicator">
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FontAwesomeIcon
                                icon={faArrowDown}
                                style={{ height: "12px", width: "12px", color:"white" }}
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faArrowUp}
                                style={{ height: "12px", width: "12px" , color:"white"}}
                              />
                            )
                          ) : null}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, rowIndex) => {
                prepareRow(row);
                return (
                  <tr 
                    {...row.getRowProps()}
                    className={`responsive-table-row ${rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      return (
                        <td
                          {...cell.getCellProps([
                            {
                              className: cell.column.className,
                              style: cell.column.style,
                            },
                          ])}
                          className={`responsive-table-td ${cell.column.className || ''}`}
                          style={{
                            borderRight: cellIndex < row.cells.length - 1 
                              ? '1px solid rgba(0,0,0,0.05)' 
                              : 'none',
                            ...cell.column.style
                          }}
                          data-label={cell.column.Header}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Mobile scroll indicator */}
        <div className="mobile-scroll-indicator">
          <span className="scroll-hint">
            <FontAwesomeIcon icon={faArrowUp} rotation={90} /> 
            Scroll horizontally to see more columns
          </span>
        </div>
      </div>

      {/* Enhanced Pagination */}
      <div className="responsive-pagination-container">
        <div className="pagination-controls">
          <div className="pagination-buttons">
            <button
              className={`button is-small is-rounded pagination-btn is-${color}`}
              onClick={() => setPage(1)}
              disabled={currentpage === 1 || loading}
              title="First Page"
            >
              <span className="pagination-btn-text">First</span>
              <span className="pagination-btn-icon">««</span>
            </button>
            
            <button
              className={`button is-small is-rounded pagination-btn is-${color}`}
              onClick={() => setPage((s) => (s === 0 ? 1 : s - 1))}
              disabled={currentpage === 1 || loading}
              title="Previous Page"
            >
              <span className="pagination-btn-text">Previous</span>
              <span className="pagination-btn-icon">«</span>
            </button>
            
            <button
              className={`button is-small is-rounded pagination-btn is-${color}`}
              onClick={() => setPage((s) => s + 1)}
              disabled={currentpage === totalPage || loading}
              title="Next Page"
            >
              <span className="pagination-btn-text">Next</span>
              <span className="pagination-btn-icon">»</span>
            </button>
            
            <button
              className={`button is-small is-rounded pagination-btn is-${color}`}
              onClick={() => setPage(totalPage)}
              disabled={currentpage === totalPage || loading}
              title="Last Page"
            >
              <span className="pagination-btn-text">Last</span>
              <span className="pagination-btn-icon">»»</span>
            </button>
          </div>
          
          <div className="pagination-info">
            <span className="pagination-text">
              Page <strong>{currentpage}</strong> of <strong>{pageOptions.length}</strong>
            </span>
          </div>
        </div>
        
        <CommonLoader loading={loading} />
      </div>
    </>
  );
}